import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik, Field, Form as FormikForm } from 'formik';
import { TextField, Switch } from 'formik-material-ui';
import { TimePicker } from 'formik-material-ui-pickers';
import {
  Grid,
  InputLabel,
  makeStyles,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  MenuItem,
  Snackbar,
  CircularProgress
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { DecimalControlledField, DateFormik, withDependencies, DateTimeFormik } from 'components/Shared/CustomInputs';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { buildYupSchema, unpackReferences } from 'utils/SchemaUtils';
// Date Utils
import DateFnsUtils from '@date-io/date-fns';
import { makePropsIterator, deepCopy } from 'utils/SchemaUtils';


const useStyles = makeStyles(theme => ({
  formField: {
    padding: 12
  },
  cardHeader: {
    "background": theme.palette.primary.main,
    "color": theme.palette.primary.contrastText,
    fontVariant: 'all-petite-caps'
  },
  VerticalFormStyle: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
      width: 'calc(100% - 16px)'
    },
    '& .Mui-disabled': {
      color: theme.palette.text.primary,
      fontWeight: 450
    }
  },
  HorizontalFormStyle: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
      width: 'calc(50% - 16px)',
    },
    '& .Mui-disabled': {
      color: theme.palette.text.primary,
      fontWeight: 450
    }
    // '& .MuiInputBase-input': {
    //   color: 'black'
    // }
  },
  CondensedFormStyleReadOnly: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
      width: 'calc(50% - 16px)'
    },
    '& .Mui-disabled': {
      color: theme.palette.text.primary
    }
  }
}));

const countDecimals = function (value) {
  if ((value % 1) !== 0)
    return value.toString().split(".")[1].length;
  return 0;
};

const fieldCreator = (jsonSchema, iter, config) => {
  const fields = [];
  const defaultValues = {};
  const errors = [];
  const inputVariant = config.inputVariant ? config.inputVariant : "outlined";
  for (const { key, props } of iter) {
    switch (props.type) {
      case "string":
        if (props.format) {
          switch (props.format) {
            case "date":
              !config.hiddenProperties.includes(key) && fields.push(
                <Field
                  name={key}
                  key={key}
                >
                  {(formikProps) => withDependencies(DateFormik,
                    {
                      childProps: {
                        format: "MM/dd/yyyy",
                        clearable: true,
                        label: props['label'],
                        inputVariant: inputVariant,
                        InputProps: {
                          disabled: config.disabledProperties.includes(key),
                          placeholder: 'Not Entered'
                        },
                        InputLabelProps: {
                          ...config.readOnly && { shrink: true }
                        },
                        key: { key }
                      }, formikProps, jsonSchema, key
                    }
                  )}
                </Field>
              )
              defaultValues[key] = props.default ? props.default : null;
              break
            case "date-time":
              !config.hiddenProperties.includes(key) && fields.push(

                <Field
                  component={DateTimeFormik}
                  name={key}
                  showTodayButton
                  clearable
                  label={props['label']}
                  inputVariant={inputVariant}
                  InputProps={{ disabled: config.disabledProperties.includes(key) }}
                  InputLabelProps={{
                    ...config.readOnly && { shrink: true }
                  }}
                  key={key}
                />

              )
              defaultValues[key] = props.default ? props.default : null;
              break
            case "time":
              !config.hiddenProperties.includes(key) && fields.push(
                <Field
                  component={TimePicker}
                  name={key}
                  showTodayButton
                  clearable
                  label={props['label']}
                  inputVariant={inputVariant}
                  InputProps={{ disabled: config.disabledProperties.includes(key) }}
                  key={key}
                  InputLabelProps={{
                    ...config.readOnly && { shrink: true }
                  }}
                />
              )
              defaultValues[key] = props.default ? props.default : null;
              break
            case "email":
              !config.hiddenProperties.includes(key) && fields.push(

                <Field
                  name={key}
                  type="text"
                  component={TextField}
                  variant={inputVariant}
                  label={key}
                  InputProps={{ disabled: config.disabledProperties.includes(key) }}
                  key={key}
                  InputLabelProps={{
                    ...config.readOnly && { shrink: true }
                  }}
                />

              )
              defaultValues[key] = props.default ? props.default : '';
              break
            default:
              !config.hiddenProperties.includes(key) && fields.push(

                <Field
                  name={key}
                  type="text"
                  component={TextField}
                  variant={inputVariant}
                  label={key}
                  InputProps={{ disabled: config.disabledProperties.includes(key) }}
                  key={key}
                  InputLabelProps={{
                    shrink: config.readOnly
                  }}
                />

              )
              defaultValues[key] = props.default ? props.default : "";
              errors.push(`property ${key} contains an unsupported format type`)
              break
          }
        } else if (props.enum) {
          const children = props.enum.map((value) => (
            <MenuItem value={value} key={value}>{value}</MenuItem>
          ))
          !config.hiddenProperties.includes(key) && fields.push(
            <Field
              name={key}
              key={key}
            >
              {(formikProps) => withDependencies(TextField,
                {
                  childProps: {
                    select: true,
                    label: props['label'],
                    variant: inputVariant,
                    InputProps: {
                      disabled: config.disabledProperties.includes(key)
                    },
                    InputLabelProps: {
                      ...config.readOnly && { shrink: true }
                    },
                    key: { key }
                  }, formikProps, jsonSchema, key, children
                }
              )}
            </Field>
          )

          defaultValues[key] = props.default ? props.default : "";
        }
        else if (props.oneOf) {
          const children = props.oneOf.map(({ const: constant, title }) => (
            <MenuItem value={constant} key={constant}>{title}</MenuItem>
          ))
          !config.hiddenProperties.includes(key) && fields.push(
            <Field
              name={key}
              key={key}
            >
              {(formikProps) => withDependencies(TextField,
                {
                  childProps: {
                    select: true,
                    label: props['label'],
                    variant: inputVariant,
                    InputProps: {
                      disabled: config.disabledProperties.includes(key)
                    },
                    InputLabelProps: {
                      ...config.readOnly && { shrink: true }
                    },
                    key: { key }
                  }, formikProps, jsonSchema, key, children
                }
              )}
            </Field>
          )
          defaultValues[key] = props.default ? props.default : "";
        } else {
          !config.hiddenProperties.includes(key) && fields.push(
            <Field
              name={key}
              key={key}
            >
              {(formikProps) => withDependencies(TextField,
                {
                  childProps: {
                    type: "text",
                    label: props['label'],
                    variant: inputVariant,
                    InputProps: {
                      disabled: config.disabledProperties.includes(key)
                    },
                    key: { key },
                    InputLabelProps: {
                      ...config.readOnly && { shrink: true }
                    },
                    placeholder: "Not Entered"
                  }, formikProps, jsonSchema, key
                }
              )}
            </Field>
          )
          defaultValues[key] = props.default ? props.default : "";
        };
        break
      case "number":
        if (props.multipleOf) {
          const percision = countDecimals(props.multipleOf);
          !config.hiddenProperties.includes(key) && fields.push(
            <Field
              name={key}
              key={key}
            >
              {(formikProps) => withDependencies(DecimalControlledField,
                {
                  childProps: {
                    type: "number",
                    label: props['label'],
                    variant: inputVariant,
                    percision,
                    InputProps: {
                      disabled: config.disabledProperties.includes(key)
                    },
                    InputLabelProps: {
                      ...config.readOnly && { shrink: true }
                    },
                    key: { key }
                  }, formikProps, jsonSchema, key
                }
              )}
            </Field>
          )

          defaultValues[key] = props.default !== undefined ? props.default : "";
        } else {
          !config.hiddenProperties.includes(key) && fields.push(
            <Field
              name={key}
              key={key}
            >
              {(formikProps) => withDependencies(DecimalControlledField,
                {
                  childProps: {
                    type: "number",
                    label: props['label'],
                    variant: inputVariant,
                    percision: 0,
                    InputProps: {
                      disabled: config.disabledProperties.includes(key)
                    },
                    InputLabelProps: {
                      ...config.readOnly && { shrink: true }
                    },
                    key: { key }
                  }, formikProps, jsonSchema, key
                }
              )}
            </Field>
          )

          defaultValues[key] = props.default !== undefined ? props.default : "";
          // errors.push(`property '${key}' is set to type: 'number' but does not contain a multipleOf definition. Reccommended to changed type to 'integer'`);
        }
        break
      case "integer":
        !config.hiddenProperties.includes(key) && fields.push(
          <Field
            name={key}
            key={key}
          >
            {(formikProps) => withDependencies(DecimalControlledField,
              {
                childProps: {
                  type: "number",
                  label: props['label'],
                  variant: inputVariant,
                  percision: 0,
                  InputProps: {
                    disabled: config.disabledProperties.includes(key)
                  },
                  key: { key },
                  InputLabelProps: {
                    ...config.readOnly && { shrink: true }
                  }
                }, formikProps, jsonSchema, key
              }
            )}
          </Field>
        )
        defaultValues[key] = props.default !== undefined ? props.default : "";
        break
      case "boolean":
        if (props.oneOf) {
          const children = props.oneOf.map(({ const: constant, title }) => (
            <MenuItem value={constant} key={constant}>{title}</MenuItem>
          ))
          !config.hiddenProperties.includes(key) && fields.push(
            <Field
              name={key}
              key={key}
            >
              {(formikProps) => withDependencies(TextField,
                {
                  childProps: {
                    select: true,
                    label: props['label'],
                    variant: inputVariant,
                    InputProps: {
                      disabled: config.disabledProperties.includes(key)
                    },
                    key: { key }
                  }, formikProps, jsonSchema, key, children
                }
              )}
            </Field>
          )
          defaultValues[key] = props.default ? Boolean(props.default) : '';
        } else {
          !config.hiddenProperties.includes(key) && fields.push(
            <div className={"MuiFormControl-root MuiTextField-root"} key={key}>
              <InputLabel htmlFor={key}>{props['label']}</InputLabel>
              <Field
                name={key}
                key={key}
              >
                {(formikProps) => withDependencies(Switch,
                  {
                    childProps: {
                      type: "checkbox",
                      label: props['label'],
                      disabled: config.disabledProperties.includes(key),
                      key: { key },
                      checked: formikProps.form.values[key]
                    }, formikProps, jsonSchema, key
                  }
                )}
              </Field>
            </div>
          )
          defaultValues[key] = props.default ? Boolean(props.default) : false;
        }
        break
      case "object":
        errors.push(`property ${key} does not match any supported Types`);
        break
      case "array":
        errors.push(`property ${key} does not match any supported Types`);
        break
      case "null":
        errors.push(`property ${key} does not match any supported Types`);
        break
      default:
        errors.push(`property ${key} does not match any supported Types`);
        break
    };
  };

  return [fields, defaultValues, errors];
}

const createFields = (jsonSchema, config) => {
  let fields = [];
  let defaultValues = {};
  let errors = [];

  const propsIterator = makePropsIterator(jsonSchema);
  const [_fields, _defaultValues, _errors] = fieldCreator(jsonSchema, propsIterator, config);
  fields = [...fields, ..._fields];
  defaultValues = { ...defaultValues, ..._defaultValues };
  errors = [...errors, ..._errors];

  if (jsonSchema.dependencies) {
    Object.keys(jsonSchema.dependencies).forEach(key => {
      const dep = jsonSchema.dependencies[key];
      const iter = makePropsIterator(dep);
      const [f, d, e] = fieldCreator(jsonSchema, iter, config);
      fields = [...fields, ...f];
      defaultValues = { ...defaultValues, ...d };
      errors = [...errors, ...e];
    });
  }

  return [fields, defaultValues, errors];
}

const NotificationsSnackBar = ({ status, setStatus, refreshLagTime, anchorOrigin={ vertical: 'bottom', horizontal: 'left' } }) => (
  status && typeof (status.success) === "boolean" ? <Snackbar
    open={typeof (status) !== "undefined"}
    autoHideDuration={refreshLagTime}
    anchorOrigin={anchorOrigin}
    onClose={() => { setStatus(undefined) }}
  >
    <Alert severity={status && status.alertType} onClose={() => { setStatus(undefined) }}>
      {status && typeof (status.success) === "boolean" && status.message}
    </Alert>
  </Snackbar> : null
)

const DynamicForm = ({ jsonSchema, onSubmit, cancel = false, onCancel = () => { }, config: _config, title = true, children }) => {
  const [config, setConfig] = useState(_config);
  const [formSession, resetFormSession] = useState(new Date());
  const mountedRef = useRef(true);
  const classes = useStyles();
  const refreshLagTime = config.refreshLagTime ? config.refreshLagTime : 1000;

  useEffect(() => {
    if (config.readOnly) {
      setConfig(oldConfig => ({ ...oldConfig, disabledProperties: Object.keys(jsonSchema.properties) }));
    } else {
      setConfig(oldConfig => ({ ...oldConfig, disabledProperties: _config.disabledProperties }))
    };
  }, [config.readOnly, _config.disabledProperties, jsonSchema.properties]);

  const [fields, defaultValues, errors] = React.useMemo(() => createFields(jsonSchema, config), [jsonSchema, config]);

  const yupSchema = buildYupSchema(jsonSchema);

  const handleSuccessfulSubmit = useCallback(async () => {
    const res = await new Promise(resolve => setTimeout(resolve, refreshLagTime));
    if (!mountedRef.current) return null;
    resetFormSession(res);
    return res
  }, [refreshLagTime]);

  useEffect(() => {
    // unsubscribe the form reset if component is unmounted
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleSubmit = async (values, { setStatus, resetForm }) => {
    const editResult = await onSubmit(values);
    if (editResult.success) {
      setStatus({ success: false, message: "Form was submitted successfully", alertType: "success" });
      await new Promise(resolve => setTimeout(resolve, refreshLagTime));
      await handleSuccessfulSubmit();
    } else {
      setStatus({ success: false, message: "There was an issue submitting the form", alertType: "error" });
      await new Promise(resolve => setTimeout(resolve, refreshLagTime));
    };

    // reset config to original passed values;
    setConfig(_config);
  }

  /**
   * handles cancel button event that makes the form 'read-only' again, also resets values
   * @param {*} event 
   */
  const handleCancelBtn = (event, resetForm) => {
    event.preventDefault();
    setConfig(oldConfig => ({ ...oldConfig, readOnly: true, inputVariant: _config.inputVariant }));
    resetForm();
  };


  const handleEditBtn = (event) => { event.preventDefault(); setConfig(oldConfig => ({ ...oldConfig, readOnly: false, inputVariant: 'outlined' })); };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Formik
        key={formSession}
        initialValues={defaultValues}
        validationSchema={yupSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {
          ({ setStatus, status, isSubmitting, values, resetForm }) => {
            return (
              <Grid container justify='center'>
                {isSubmitting ? <CircularProgress style={{ margin: 24 }} /> :
                  <Grid item xs={12}>
                    <Card elevation={config.cardElevation} style={config.cardStyles}>
                      <FormikForm
                        noValidate
                        className={config.formLayout === "Horizontal" ? classes.HorizontalFormStyle : classes.VerticalFormStyle}
                        autoComplete="off"
                      >
                        {
                          title &&
                          <CardHeader title={jsonSchema.title} className={classes.cardHeader}></CardHeader>
                        }
                        <CardContent>
                          {errors.map((message, index) => <Alert severity="error" key={index}>{message}</Alert>)}
                          {fields.map(r => {
                            if (!config.fieldDecorators || !config.fieldDecorators[r.props.name]) {
                              return r;
                            } else {
                              const size = config.fieldDecorators[r.props.name].size || 4;
                              return <Grid container direction="row" justify="center" alignItems="center" key={r.props.name}>
                                <Grid item xs={12 - size}>{r}</Grid>
                                <Grid item xs={size}>{config.fieldDecorators[r.props.name].component}
                                </Grid>
                              </Grid>
                            }

                          })}
                        </CardContent>
                        {
                          !config.hideActions &&
                            !config.readOnly ?
                              <CardActions>
                                <div className={classes.formField}>
                                  <Button variant="contained" color="primary" type="submit" disabled={config.disabled}>Submit</Button>
                                  {cancel && <Button variant="contained" color="default" style={{ marginLeft: 12 }} onClick={onCancel}>Cancel</Button> /* this cancel refers to cancels within modals*/}
                                  {config.readOnlyEdit && <Button variant="contained" color="default" style={{ marginLeft: 12 }} onClick={(event) => handleCancelBtn(event, resetForm)}>Cancel</Button> /* this cancel refers to cancels within readonly forms */}
                                </div>
                              </CardActions> :
                              <CardActions>
                                <div className={classes.formField}>
                                  {config.readOnlyEdit && <Button variant="contained" color="primary" onClick={handleEditBtn}>Edit</Button>}
                                </div>
                              </CardActions>
                        }
                      </FormikForm>
                    </Card>
                  </Grid>
                }
                <NotificationsSnackBar status={status} setStatus={setStatus} refreshLagTime={refreshLagTime} />
                {children}
              </Grid>
            )
          }
        }
      </Formik>
    </MuiPickersUtilsProvider>
  );

};

DynamicForm.propTypes = {
  jsonSchema: PropTypes.object,
  onSubmit: PropTypes.func,
  config: PropTypes.object
};

DynamicForm.defaultProps = {
  jsonSchema: {},
  onSubmit: async () => { return { success: false } },
  config: {
    disabledProperties: [],
    hiddenProperties: [],
    formLayout: "Vertical",
    refreshLagTime: 1000,
    cardElevation: 2,
    cardStyle: {}
  }
}

const withExtension = (ChildComponent, { schema, orientation = "Vertical", defaultValues = {}, onSubmit, 
    disabledProperties = [], hiddenProperties = [], definitions, readOnly = false, 
    readOnlyEdit = false, cancel = false, onCancel = () => { }, inputVariant = "outlined", 
    children, fieldDecorators = {}, title, disabled = false, refreshLagTime = 1000, 
    cardElevation = 2, cardStyles = {}, hideActions = false
}) => {
  const unModifiedSchema = deepCopy(schema);
  let modifiedSchema = deepCopy(unModifiedSchema);
  if (definitions) { modifiedSchema = unpackReferences(modifiedSchema, definitions) };


  // set default values if provided
  Object.keys(defaultValues).forEach((item) => {
    if (Object.keys(modifiedSchema.properties).includes(item)) {
      modifiedSchema.properties[item].default = defaultValues[item];
    }

    if (modifiedSchema.dependencies && Object.keys(modifiedSchema.dependencies).length > 0 && Object.keys(modifiedSchema.dependencies).includes(item)) {
      const dependent = modifiedSchema.dependencies[item];
      Object.keys(dependent.properties).forEach((key, index) => {
        dependent.properties[key].default = defaultValues[key];
      });
    }
  });

  if (readOnly) {
    inputVariant = "filled"
  }

  Object.keys(modifiedSchema.properties).forEach((item) => {
    if (modifiedSchema.properties[item].options) {
      if (modifiedSchema.properties[item].options.hidden) {
        hiddenProperties.push(item);
      }
    }
  });

  // if (readOnly) {
  //   Object.keys(modifiedSchema.properties).forEach((item) => {
  //     if (!modifiedSchema.properties[item].default) {
  //       modifiedSchema.properties[item].default = modifiedSchema.properties[item].format !== "date" ? "Not Entered" : null;
  //     }
  //   });
  // };

  const config = {
    "disabledProperties": disabledProperties,
    "hiddenProperties": hiddenProperties,
    "formLayout": orientation,
    "readOnly": readOnly,
    "readOnlyEdit": readOnlyEdit,
    "inputVariant": inputVariant,
    fieldDecorators: fieldDecorators,
    disabled,
    refreshLagTime: refreshLagTime,
    cardElevation: cardElevation,
    cardStyles: cardStyles,
    hideActions: hideActions
  };

  return (
    <ChildComponent jsonSchema={modifiedSchema} onSubmit={onSubmit} config={config} cancel={cancel} onCancel={onCancel} title={(readOnly && !title) || !title ? false : true}>{children}</ChildComponent>
  )
}

export default DynamicForm;
export { withExtension, useStyles, NotificationsSnackBar };